import React, { useState, useCallback, useRef, useEffect } from 'react';
import OnboardingLayout from '../components/onboarding/OnboardingLayout';
import CompletionScreen from '../components/onboarding/CompletionScreen';
import BuildingProfileOverlay from '../components/onboarding/BuildingProfileOverlay';
import { ONBOARDING_STEPS } from '../components/onboarding/onboardingSteps';
import { completeOnboarding, buildIdentity, pollBuildStatus } from '../components/onboarding/onboardingApi';
import YouTubeOnboardingStep from '../components/onboarding/steps/YouTubeOnboardingStep';
import type { YouTubeStepResult } from '../components/onboarding/steps/YouTubeOnboardingStep';

/**
 * Auto-build phase that runs BEFORE the regular onboarding steps.
 * Phase 'youtube' = Step 0A.
 * Once complete (or skipped), the flow moves into the normal ONBOARDING_STEPS.
 */
type AutoBuildPhase = 'youtube' | null;

const ONBOARDING_STORAGE_KEY = 'onboarding_progress';

interface SavedProgress {
  autoBuildPhase: AutoBuildPhase;
  stepIndex: number;
  buildResult: BuildResult | null;
}

function loadProgress(): SavedProgress | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as SavedProgress;
  } catch {
    return null;
  }
}

function saveProgress(progress: SavedProgress) {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Storage full or unavailable — non-fatal
  }
}

function clearProgress() {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch {
    // Non-fatal
  }
}

interface OnboardingProps {
  user: { hasCompletedOnboarding?: boolean } | null;
  onComplete: () => void;
  onLogout?: () => void;
}

export interface BuildResult {
  identity: {
    name: string;
    profession: string;
    defineArtist: string;
    shortDescription: string;
    heroLine: string;
    metaDescription: string;
    about: string;
    aiEnhanced?: boolean;
  };
  channelImage?: string;
  aboutImage?: string;
  spotifyImage?: string;
  wikipediaImage?: string;
  wikipedia?: {
    title: string;
    url: string;
    description?: string;
  } | null;
  spotify?: {
    artistId: string;
    name: string;
    url: string;
    image?: string;
    followers?: number | null;
    genres?: string[];
  } | null;
  songs: { title: string; youtubeVideoId: string | null; spotifyTrackId: string | null }[];
  socialLinks: Record<string, string | null>;
  songsImported: number;
  totalSongs: number;
  prefill: {
    heroCreated: boolean;
    aboutCreated: boolean;
    songsInserted: number;
    platformsInserted: number;
  };
  /** Media gallery prefill image — from serper KG / Wikipedia (NOT Spotify) */
  media?: { image: string; title: string } | null;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete, onLogout }) => {
  // ── Restore saved progress ──
  const saved = loadProgress();

  // ── Auto-build state ──
  const [autoBuildPhase, setAutoBuildPhase] = useState<AutoBuildPhase>(saved ? saved.autoBuildPhase : 'youtube');
  const [, setYoutubeResult] = useState<YouTubeStepResult | null>(null);
  const [buildResult, setBuildResult] = useState<BuildResult | null>(saved?.buildResult ?? null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [buildError, setBuildError] = useState('');

  // ── Background status polling ──
  const [backgroundStatus, setBackgroundStatus] = useState<{ musicReady: boolean; socialReady: boolean } | null>(null);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start polling when build completes
  useEffect(() => {
    if (!buildResult) return;

    const poll = async () => {
      try {
        const data = await pollBuildStatus();
        if (data.success) {
          setBackgroundStatus({ musicReady: data.musicReady, socialReady: data.socialReady });
          if (data.musicReady && data.socialReady && pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
          }
        }
      } catch {
        // Non-fatal — keep polling
      }
    };

    pollingRef.current = setInterval(poll, 5000);
    // Also poll immediately
    poll();

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [buildResult]);

  // ── Regular onboarding state ──
  const [stepIndex, setStepIndex] = useState(saved?.stepIndex ?? 0);
  const [isValid, setIsValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [dismissed, _setDismissed] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [previewPatch, setPreviewPatch] = useState<Record<string, unknown>>({});
  const handlePreviewChange = useCallback((patch: Record<string, unknown>) => {
    setPreviewPatch((current) => ({ ...current, ...patch }));
  }, []);
  const saveRef = useRef<(() => Promise<void>) | null>(null);

  // Lock body scroll while onboarding is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // ── Persist progress on state changes ──
  useEffect(() => {
    saveProgress({ autoBuildPhase, stepIndex, buildResult });
  }, [autoBuildPhase, stepIndex, buildResult]);

  const handleValidationChange = useCallback((valid: boolean) => {
    setIsValid(valid);
  }, []);

  // ── Auto-build: YouTube resolved → trigger build immediately ──
  const handleYouTubeResolved = useCallback(async (result: YouTubeStepResult) => {
    setYoutubeResult(result);
    setIsBuilding(true);
    setBuildError('');
    try {
      const data = await buildIdentity(result.channelInput, {
        wikipediaUrl: result.wikipediaUrl,
        spotifyUrl: result.spotifyUrl,
        channelOnly: result.channelOnly,
      });
      if (data.success) {
        console.log('[Onboarding] buildIdentity response:', { songs: data.songs?.length, totalSongs: data.totalSongs, songsImported: data.songsImported, keys: Object.keys(data) });
        setBuildResult(data);
      }
    } catch (err: any) {
      setBuildError(err.response?.data?.message || 'Profile build failed. You can fill in details manually.');
    } finally {
      setIsBuilding(false);
      setAutoBuildPhase(null);
      setIsValid(false);
      saveRef.current = null;
    }
  }, []);

  // ── Auto-build: Skip YouTube entirely ──
  const handleSkipAutoBuild = useCallback(() => {
    setAutoBuildPhase(null);
    setIsValid(false);
    saveRef.current = null;
  }, []);

  // ── Regular step: Continue ──
  const handleContinue = useCallback(async () => {
    // If in auto-build phase, trigger the step's save which calls onResolved/onConfirmed
    if (autoBuildPhase) {
      setIsSaving(true);
      try {
        if (saveRef.current) {
          await saveRef.current();
        }
      } catch {
        // Non-fatal
      } finally {
        setIsSaving(false);
      }
      return;
    }

    setIsSaving(true);
    setCompletionError('');
    try {
      if (saveRef.current) {
        await saveRef.current();
      }

      if (stepIndex >= ONBOARDING_STEPS.length - 1) {
        await completeOnboarding();
        clearProgress();
        onComplete(); // Update parent state (hasCompletedOnboarding + approvalStatus)
        setShowCompletion(true);
      } else {
        setStepIndex((prev) => prev + 1);
        setIsValid(false);
        saveRef.current = null;
      }
    } catch {
      if (stepIndex >= ONBOARDING_STEPS.length - 1) {
        setCompletionError('Something went wrong. Please try again.');
      } else {
        setStepIndex((prev) => prev + 1);
        setIsValid(false);
        saveRef.current = null;
      }
    } finally {
      setIsSaving(false);
    }
  }, [stepIndex, autoBuildPhase, onComplete]);

  const handleSkip = useCallback(async () => {
    // Skip during auto-build phase
    if (autoBuildPhase === 'youtube') {
      handleSkipAutoBuild();
      return;
    }

    setCompletionError('');
    if (stepIndex >= ONBOARDING_STEPS.length - 1) {
      try {
        await completeOnboarding();
        clearProgress();
        onComplete();
        setShowCompletion(true);
      } catch {
        setCompletionError('Something went wrong. Please try again.');
      }
    } else {
      setStepIndex((prev) => prev + 1);
      setIsValid(false);
      saveRef.current = null;
    }
  }, [stepIndex, autoBuildPhase, handleSkipAutoBuild]);

  const handleLogoutFromCompletion = useCallback(() => {
    clearProgress();
    document.body.style.overflow = '';
    if (onLogout) onLogout();
  }, [onLogout]);

  // ── Compute step display values ──
  const TOTAL_DISPLAY_STEPS = ONBOARDING_STEPS.length + 1; // +1 for YT

  let displayStepIndex: number;
  let title: string;
  let description: string;
  let canGoBack: boolean;
  let content: React.ReactNode;

  if (autoBuildPhase === 'youtube') {
    displayStepIndex = 0;
    title = 'Enter or Search your official YouTube channel';
    description = 'We\'ll auto-build your profile.';
    canGoBack = false;
    content = (
      <YouTubeOnboardingStep
        key="youtube-step"
        saveRef={saveRef}
        onValidationChange={handleValidationChange}
        onResolved={handleYouTubeResolved}
        onSkip={handleSkipAutoBuild}
      />
    );
  } else {
    // Regular onboarding steps (offset by 1 for progress display)
    displayStepIndex = stepIndex + 1;
    const currentStep = ONBOARDING_STEPS[stepIndex];
    title = currentStep?.title || '';
    description = currentStep?.description || '';
    canGoBack = stepIndex > 0; // No back on first step — YouTube build is locked in
    const StepComponent = currentStep?.component;
    content = StepComponent ? (
      <StepComponent
        key={currentStep.id}
        saveRef={saveRef}
        onValidationChange={handleValidationChange}
        onPreviewChange={handlePreviewChange}
        buildResult={buildResult}
        backgroundStatus={backgroundStatus ?? undefined}
      />
    ) : null;
  }

  const handleBack = () => {
    if (!autoBuildPhase && stepIndex > 0) {
      setStepIndex((i) => i - 1);
      setIsValid(false);
      saveRef.current = null;
    }
  };

  if (dismissed) return null;

  const currentStep = autoBuildPhase ? null : ONBOARDING_STEPS[stepIndex];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/50 backdrop-blur-sm p-2 sm:p-4">
      <div className="relative flex h-full w-full flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:h-[96vh]">
        {/* Building overlay */}
        <BuildingProfileOverlay visible={isBuilding} />

        {showCompletion ? (
          <CompletionScreen onLogout={handleLogoutFromCompletion} />
        ) : (
          <>
          {completionError && (
            <div className="px-6 pt-4">
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                {completionError}
              </div>
            </div>
          )}
          {buildError && (
            <div className="px-6 pt-4">
              <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                {buildError}
              </div>
            </div>
          )}
          <OnboardingLayout
            currentStep={displayStepIndex}
            totalSteps={TOTAL_DISPLAY_STEPS}
            title={title}
            description={description}
            showLivePreview={!autoBuildPhase}
            previewSection={currentStep?.id}
            previewPatch={previewPatch}
            isValid={isValid}
            isSaving={isSaving}
            onContinue={handleContinue}
            onSkip={handleSkip}
            onBack={canGoBack ? handleBack : undefined}
            showSkip={autoBuildPhase === 'youtube' ? false : (!!currentStep?.skippable && !isValid)}
          >
            {content}
          </OnboardingLayout>
          </>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
